import { useId, useState } from 'react';
import { Drawer } from 'antd';
import { DeleteOutlined, EditOutlined, EyeOutlined, PlusOutlined, SafetyCertificateOutlined, SearchOutlined, TagsOutlined, ToolOutlined } from '@ant-design/icons';

import { Bento, DataTable, MenuThaoTac, StatTile, Tile } from '../../../../components/shared';
import { Badge, Button, Dialog, Empty, Field, Input, Select, Skeleton, Textarea } from '../../../../components/ui';
import { useLoi } from '../../../../hooks/useLoi';
import { useT } from '../../../../i18n';
import { KHONG_CO, ngayGioVN, soVN } from '../../../../lib/dinh-dang';
import { maLoi } from '../../../../lib/errors';
import { useToast } from '../../../../lib/toast';
import { HopXacNhanThaoTac } from '../../../nguoi-dung/components/HopXacNhanThaoTac';
import { LoiTrongHop } from '../../../tenant/components/phan-form';
import { LoiRpa } from '../chung';
import { NHAN_ROLE } from '../../constants';
import { useRolesOpenCore, useSuaRoleOpenCore, useTaoRoleOpenCore, useUsersOpenCore, useXoaRoleOpenCore } from '../../hooks/useDanhBaOpenCore';
import { GIOI_HAN_ROLE, locRole, thanRole, thanhVienThem } from '../../nghiep-vu/danh-ba';
import type { RoleOpenCore } from '../../types';
import { TenRole } from './nhan';

type Hop = { kieu: 'tao' } | { kieu: 'sua' | 'xoa'; role: RoleOpenCore } | null;

/**
 * Tab Role của màn User OpenCore (`OpenCoreRolesPanel` của native): role mà tài khoản dịch vụ OpenCore đọc được. Sửa,
 * xoá theo `rpa:opencore-role:update`; role hệ thống không xoá, không đổi tên; thành viên role License do màn License
 * đồng bộ.
 */
export function TabRole({ suaDuoc, docUserDuoc }: { suaDuoc: boolean; docUserDuoc: boolean }) {
  const q = useRolesOpenCore();
  if (q.isPending) {
    return (
      <div className="space-y-4" aria-busy="true">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (q.isError) {
    return (
      <Tile>
        <LoiRpa loi={q.error} onThuLai={() => void q.refetch()} />
      </Tile>
    );
  }
  return <NoiDung roles={q.data} suaDuoc={suaDuoc} docUserDuoc={docUserDuoc} />;
}

function NoiDung({ roles, suaDuoc, docUserDuoc }: { roles: RoleOpenCore[]; suaDuoc: boolean; docUserDuoc: boolean }) {
  const { t, locale } = useT();
  const [tim, setTim] = useState('');
  const [hop, setHop] = useState<Hop>(null);
  const [dangXem, setDangXem] = useState<string | null>(null);
  const heThong = roles.filter((r) => r.system).length;
  const loc = locRole(roles, tim);
  const xem = dangXem ? roles.find((r) => r.id === dangXem) : undefined;

  return (
    <div className="space-y-4">
      <Bento>
        <StatTile span="sm:col-span-4 lg:col-span-4" nhan={t('rpaDanhBa.kpiRole')} so={soVN(roles.length, locale)} icon={<TagsOutlined />} tone="primary" />
        <StatTile span="sm:col-span-4 lg:col-span-4" nhan={t('rpaDanhBa.kpiRoleHeThong')} so={soVN(heThong, locale)} icon={<SafetyCertificateOutlined />} tone="violet" />
        <StatTile span="sm:col-span-4 lg:col-span-4" nhan={t('rpaDanhBa.kpiRoleTuyChinh')} so={soVN(roles.length - heThong, locale)} icon={<ToolOutlined />} tone="neutral" />
      </Bento>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <Input
          allowClear
          autoComplete="off"
          prefix={<SearchOutlined aria-hidden />}
          placeholder={t('rpaDanhBa.timRole')}
          aria-label={t('rpaDanhBa.timRole')}
          value={tim}
          onChange={(e) => setTim(e.target.value)}
          className="w-full sm:w-72"
        />
        {suaDuoc && (
          <Button variant="primary" size="md" icon={<PlusOutlined />} onClick={() => setHop({ kieu: 'tao' })}>
            {t('rpaDanhBa.taoRole')}
          </Button>
        )}
      </div>
      <p className="text-xs text-content-muted">{t('rpaDanhBa.roleGoiY')}</p>

      <Tile className="min-w-0">
        {roles.length === 0 ? (
          <Empty
            icon={TagsOutlined}
            title={t('rpaDanhBa.rongRole')}
            description={t('rpaDanhBa.rongRoleMoTa')}
            action={
              suaDuoc ? (
                <Button variant="primary" size="sm" icon={<PlusOutlined />} onClick={() => setHop({ kieu: 'tao' })}>
                  {t('rpaDanhBa.taoRole')}
                </Button>
              ) : undefined
            }
          />
        ) : loc.length === 0 ? (
          <Empty icon={SearchOutlined} title={t('rpaDanhBa.rongLoc')} />
        ) : (
          <DataTable<RoleOpenCore>
            caption={t('rpaDanhBa.tabRole')}
            dataSource={loc}
            rowKey="id"
            pagination={loc.length > 20 ? { pageSize: 20, showSizeChanger: false } : false}
            columns={[
              { title: t('rpaDanhBa.cotTenRole'), key: 'name', render: (_, r) => <TenRole role={r} /> },
              { title: t('rpaDanhBa.cotId'), key: 'id', render: (_, r) => <code className="whitespace-nowrap font-mono text-2xs text-content-muted">{r.id}</code> },
              {
                title: t('rpaDanhBa.cotThanhVien'),
                key: 'members',
                align: 'right',
                width: 120,
                render: (_, r) => <span className="tabular-nums">{soVN(r.memberCount, locale)}</span>,
              },
              {
                title: t('rpaDanhBa.cotMoTa'),
                key: 'description',
                render: (_, r) => (
                  <span className="line-clamp-2 max-w-xs text-sm text-content-secondary" title={r.description}>
                    {r.description || KHONG_CO}
                  </span>
                ),
              },
              {
                title: t('rpaDanhBa.cotLoai'),
                key: 'loai',
                render: (_, r) => {
                  const v = NHAN_ROLE[r.system ? 'system' : 'custom'];
                  return <Badge variant={v.variant}>{t(v.khoa)}</Badge>;
                },
              },
              {
                title: <span className="sr-only">{t('chung.thaoTac')}</span>,
                key: 'thaoTac',
                width: 72,
                align: 'center',
                fixed: 'right',
                render: (_, r) => (
                  <MenuThaoTac
                    tenBanGhi={r.name}
                    muc={[
                      { khoa: 'xem', nhan: t('rpaDanhBa.xem'), icon: <EyeOutlined />, nhom: 'xem', onChon: () => setDangXem(r.id) },
                      { khoa: 'sua', nhan: t('rpaDanhBa.sua'), icon: <EditOutlined />, nhom: 'sua', hien: suaDuoc, onChon: () => setHop({ kieu: 'sua', role: r }) },
                      {
                        khoa: 'xoa',
                        nhan: t('rpaDanhBa.xoaRole'),
                        icon: <DeleteOutlined />,
                        nhom: 'pha-huy',
                        hien: suaDuoc,
                        voHieu: r.system,
                        ...(r.system ? { moTa: t('rpaDanhBa.roleHeThongKhongXoa') } : {}),
                        onChon: () => setHop({ kieu: 'xoa', role: r }),
                      },
                    ]}
                  />
                ),
              },
            ]}
          />
        )}
      </Tile>

      {(hop?.kieu === 'tao' || hop?.kieu === 'sua') && <HopRole goc={hop.kieu === 'sua' ? hop.role : null} docUserDuoc={docUserDuoc} onClose={() => setHop(null)} />}
      {hop?.kieu === 'xoa' && <HopXoaRole role={hop.role} onClose={() => setHop(null)} />}
      {xem && (
        <Drawer
          open
          onClose={() => setDangXem(null)}
          placement="right"
          size="min(560px, 100vw)"
          destroyOnHidden
          title={<TenRole role={xem} />}
          footer={
            suaDuoc ? (
              <div className="flex justify-end">
                <Button
                  variant="primary"
                  size="md"
                  icon={<EditOutlined />}
                  onClick={() => {
                    setDangXem(null);
                    setHop({ kieu: 'sua', role: xem });
                  }}
                >
                  {t('rpaDanhBa.sua')}
                </Button>
              </div>
            ) : undefined
          }
        >
          <dl className="grid grid-cols-1 gap-3">
            <div>
              <dt className="text-xs text-content-muted">{t('rpaDanhBa.cotId')}</dt>
              <dd>
                <code className="break-all text-xs">{xem.id}</code>
              </dd>
            </div>
            <div>
              <dt className="text-xs text-content-muted">{t('rpaDanhBa.cotMoTa')}</dt>
              <dd className="whitespace-pre-wrap text-sm">{xem.description || KHONG_CO}</dd>
            </div>
            <div>
              <dt className="text-xs text-content-muted">{t('rpaDanhBa.ngan.suaLuc')}</dt>
              <dd className="text-sm tabular-nums">{ngayGioVN(xem.modifiedAt, locale)}</dd>
            </div>
            <div>
              <dt className="mb-1 text-xs text-content-muted">{t('rpaDanhBa.ngan.thanhVien', { so: soVN(xem.memberCount, locale) })}</dt>
              <dd>
                {xem.members.length ? (
                  <ul className="divide-y divide-default rounded-lg border border-default">
                    {xem.members.map((m) => (
                      <li key={m.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                        <span className="min-w-0 truncate">{m.name || KHONG_CO}</span>
                        <code className="shrink-0 text-xs text-content-muted">{m.id}</code>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <span className="text-sm text-content-muted">{t('rpaDanhBa.ngan.chuaCoThanhVien')}</span>
                )}
              </dd>
            </div>
          </dl>
        </Drawer>
      )}
    </div>
  );
}

const CAN_XAC_NHAN = 'RPA_OPENCORE_ADMIN_CONFIRM_REQUIRED';

/** Tạo / sửa role (`OpenCoreRoleFormDialog` của native); thêm thành viên vào `admins` thì hỏi lại. */
function HopRole({ goc, docUserDuoc, onClose }: { goc: RoleOpenCore | null; docUserDuoc: boolean; onClose: () => void }) {
  const { t } = useT();
  const toast = useToast();
  const dichLoi = useLoi();
  const formId = useId();
  // Danh sách User để chọn thành viên: máy chủ cho đọc bằng opencore-user:read hoặc license:read.
  const users = useUsersOpenCore(docUserDuoc);
  const tao = useTaoRoleOpenCore();
  const sua = useSuaRoleOpenCore();
  const dang = goc ? sua : tao;
  const cu = goc?.members.map((m) => m.id) ?? [];
  const [ten, setTen] = useState(goc?.name ?? '');
  const [moTa, setMoTa] = useState(goc?.description ?? '');
  const [thanhVien, setThanhVien] = useState<string[]>(cu);
  const [loiTen, setLoiTen] = useState(false);
  const [hoiAdmin, setHoiAdmin] = useState(false);
  const khoaThanhVien = goc?.managedBy === 'license';

  // Thành viên có thể là User mà tài khoản dịch vụ không đọc được: vẫn giữ để không gỡ nhầm.
  const biet = new Set((users.data?.data ?? []).map((u) => u.id));
  const luaChon = [
    ...(users.data?.data ?? []).map((u) => ({ value: u.id, label: `${u.name || u.username} (${u.username || u.id})` })),
    ...(goc?.members ?? []).filter((m) => !biet.has(m.id)).map((m) => ({ value: m.id, label: m.name || m.id })),
  ];

  const gui = (xacNhanAdmin: boolean) => {
    if (!ten.trim()) {
      setLoiTen(true);
      return;
    }
    if (dang.isPending) return;
    if (!xacNhanAdmin && goc?.isAdmins && thanhVienThem(thanhVien, cu).length > 0) {
      setHoiAdmin(true);
      return;
    }
    const than = thanRole({ name: ten, description: moTa, memberIds: thanhVien }, goc, xacNhanAdmin);
    const xong = {
      onSuccess: () => {
        toast.success(t(goc ? 'rpaDanhBa.roleForm.daLuu' : 'rpaDanhBa.roleForm.daTao', { ten: than.name }));
        setHoiAdmin(false);
        onClose();
      },
      onError: (e: unknown) => setHoiAdmin(maLoi(e) === CAN_XAC_NHAN && !xacNhanAdmin),
    };
    if (goc) sua.mutate({ id: goc.id, than }, xong);
    else tao.mutate(than, xong);
  };

  const loiChung = dang.error && maLoi(dang.error) !== CAN_XAC_NHAN ? dichLoi(dang.error) : null;
  const tenThem = thanhVienThem(thanhVien, cu).map((id) => luaChon.find((o) => o.value === id)?.label ?? id);

  return (
    <>
      <Dialog
        open
        onClose={onClose}
        width={640}
        destroyOnHidden
        title={goc ? t('rpaDanhBa.roleForm.suaTieuDe', { ten: goc.name }) : t('rpaDanhBa.roleForm.taoTieuDe')}
        footer={
          <div className="space-y-3">
            {loiChung && <LoiTrongHop>{loiChung}</LoiTrongHop>}
            <div className="flex flex-wrap justify-end gap-3">
              <Button variant="outline" size="md" onClick={onClose}>
                {t('chung.huy')}
              </Button>
              <Button variant="primary" size="md" htmlType="submit" form={formId} loading={dang.isPending && !hoiAdmin}>
                {dang.isPending ? t('chung.dangLuu') : t(goc ? 'rpaDanhBa.roleForm.luu' : 'rpaDanhBa.roleForm.tao')}
              </Button>
            </div>
          </div>
        }
      >
        <form
          id={formId}
          noValidate
          className="space-y-4 pt-2"
          onSubmit={(e) => {
            e.preventDefault();
            gui(false);
          }}
        >
          <Field
            label={t('rpaDanhBa.roleForm.ten')}
            required
            hint={goc?.system ? t('rpaDanhBa.roleForm.tenHeThong') : undefined}
            error={loiTen ? t('rpaDanhBa.roleForm.loiTen') : undefined}
          >
            {(p) => (
              <Input
                {...p}
                autoFocus={!goc?.system}
                autoComplete="off"
                maxLength={GIOI_HAN_ROLE.ten}
                disabled={goc?.system}
                value={ten}
                onChange={(e) => {
                  setTen(e.target.value);
                  setLoiTen(false);
                  dang.reset();
                }}
              />
            )}
          </Field>
          <Field label={t('rpaDanhBa.roleForm.moTa')}>
            {(p) => <Textarea {...p} rows={3} maxLength={GIOI_HAN_ROLE.moTa} value={moTa} onChange={(e) => setMoTa(e.target.value)} />}
          </Field>
          <Field
            label={t('rpaDanhBa.roleForm.thanhVien')}
            hint={
              khoaThanhVien
                ? t('rpaDanhBa.roleForm.thanhVienLicense')
                : !docUserDuoc
                  ? t('rpaDanhBa.roleForm.thieuQuyenUser')
                  : users.isError
                    ? dichLoi(users.error)
                    : goc?.isAdmins
                      ? t('rpaDanhBa.roleForm.adminsGoiY')
                      : undefined
            }
          >
            {(p) => (
              <Select<string[]>
                id={p.id}
                aria-describedby={p['aria-describedby']}
                className="w-full"
                mode="multiple"
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder={t('rpaDanhBa.roleForm.chonUser')}
                loading={docUserDuoc && users.isPending}
                disabled={khoaThanhVien}
                value={thanhVien}
                onChange={(ids) => {
                  setThanhVien(ids);
                  dang.reset();
                }}
                options={luaChon}
              />
            )}
          </Field>
        </form>
      </Dialog>
      {hoiAdmin && (
        <HopXacNhanThaoTac
          open
          nguyHiem
          tieuDe={t('rpaDanhBa.admin.tieuDe')}
          than={t('rpaDanhBa.admin.than', { ten: tenThem.join(', ') })}
          nhanXacNhan={t('rpaDanhBa.admin.nut')}
          dangChay={dang.isPending}
          loi={null}
          onClose={() => setHoiAdmin(false)}
          onXacNhan={() => gui(true)}
        />
      )}
    </>
  );
}

function HopXoaRole({ role, onClose }: { role: RoleOpenCore; onClose: () => void }) {
  const { t, locale } = useT();
  const toast = useToast();
  const dichLoi = useLoi();
  const xoa = useXoaRoleOpenCore();
  return (
    <HopXacNhanThaoTac
      open
      nguyHiem
      tieuDe={t('rpaDanhBa.xacNhan.xoaRoleTieuDe', { ten: role.name })}
      than={t('rpaDanhBa.xacNhan.xoaRoleThan', { so: soVN(role.memberCount, locale) })}
      nhanXacNhan={t('rpaDanhBa.xoaRole')}
      dangChay={xoa.isPending}
      loi={xoa.error ? dichLoi(xoa.error) : null}
      onClose={onClose}
      onXacNhan={() =>
        xoa.mutate(role.id, {
          onSuccess: () => {
            toast.success(t('rpaDanhBa.xacNhan.daXoaRole', { ten: role.name }));
            onClose();
          },
        })
      }
    />
  );
}
